/**
 * Utility class for random
 */
export class RNG {
    /**
     * Gets a random item from an array
     * @param array the array to get an item from
     * @returns the random item; null if the array is empty
     */
    static getItem<T>(array: T[]): T | null {
        if (!array.length) {
            return null;
        }
        return array[Math.floor(Math.random() * array.length)];
    }

    /**
     * Gets random unique items from an array
     * @param array the array to get items from
     * @param amount the amount of items to get
     * @returns the random item; null if the array is empty
     */
    static getItems<T>(array: T[], amount: number): T[] {
        if (!array.length) {
            return [];
        }

        if (amount > array.length) {
            amount = array.length;
        }

        const result = new Set<T>();
        while (result.size < amount) {
            result.add(array[Math.floor(Math.random() * array.length)]);
        }
        return Array.from(result);
    }

    /**
     * Returns a uniform distributed random value between lowerBound and upperBound
     * @param {number} lowerBound The lower end of the range to return a value from
     * @param {number} upperBound The upper end of the range to return a value from
     * @returns random value between lowerBound and upperBound
     */
    static getUniform(lowerBound: number, upperBound: number) {
        return Math.random() * (upperBound - lowerBound) + lowerBound;
    }

    /**
     * Returns a random integer rounded value between lowerBound and upperBound
     * @param {number} lowerBound The lower end of the range to return a value from, inclusive
     * @param {number} upperBound The upper end of the range to return a value from, inclusive
     * @returns random value between lowerBound and upperBound
     */
    static getUniformInt(lowerBound: number, upperBound: number) {
        let max = Math.max(lowerBound, upperBound);
        let min = Math.min(lowerBound, upperBound);
        return Math.floor(Math.random() * (max - min + 1)) + min;
    }

    static randomNonRepeatingValues(
        min: number,
        max: number,
        valueCount: number
    ): number[] {
        const count = max - min;
        const values = new Array<number>(count);
        for (let x = 0; x < count; x++) {
            values[x] = x + min;
        }

        RNG.shuffleArray(values);

        let results = [];

        for (let x = 0; x < valueCount && x < values.length; x++) {
            results.push(values[x]);
        }
        return results;
    }

    static shuffleArray<T>(array: T[]) {
        // shuffle array
        for (let x = array.length - 1; x >= 0; x--) {
            let index = RNG.getUniformInt(0, x); // Second argument is inclusive
            const temp: T = array[x];
            array[x] = array[index];
            array[index] = temp;
        }
    }
}
